/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useCallback, useEffect, useMemo, useState } from "react";
import LoadingWrapper from "../../../components/LoadingWrapper";
import { Button, Dropdown, Form, InputGroup, Modal } from "react-bootstrap";
import QuickTable from "../../../components/QuickTable";
import moment from "moment";
import { DirectDepositDocuments, reactPdfToBase64, taxDocumentDictionary } from "../../../tools";
import PDFMerger from "pdf-merger-js";
import { downloadDocumentRequestOnboarding } from "../../../services/HRDocumentsService";
import { dismissDocumentRequestOnboarding, getDashboardOnboarding } from "../../../services/DashboardService";
import DocumentRequestElement from "./DocumentRequestElement";
import DocumentRequestDetailsOnboarding from "./DocumentRequestDetailsOnboarding";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faFilter, faMagnifyingGlass, faShield } from "@fortawesome/free-solid-svg-icons";

import { Document, Image, Page, Text } from "@react-pdf/renderer";
import { useStateWithCookies } from "../../../components/useStateWithCookies";

export default function DashboardTC2() {
  const [isLoading, setIsLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDocumentRequest, setSelectedDocumentRequest] = useState(false);
  const [requestsAwaitingAdmin, setRequestsAwaitingAdmin] = useState([]);
  const [requestsAwaitingEmployee, setRequestsAwaitingEmployee] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIdentifiers, setSelectedCompanyIdentifiers] = useStateWithCookies("old-admin-dashboard-bl-selected-companies", []); // number[]

  const [filter, setFilter] = useState("");
  const [filterType, setFilterType] = useState("Company");
  const [pendingEVerify, setPendingEVerify] = useState(false);
  const [pendingPaychex, setPendingPaychex] = useState(false);

  const filterRequests = useCallback(
    (requests) => {
      const term = filter.toLowerCase();
      const selectedCompanyNames = companies.filter((c) => selectedCompanyIdentifiers.includes(c.uid)).map((c) => c.name.toLowerCase());

      return requests.filter((r) => {
        const employee = r.employee;

        // TEXT FILTER
        let textMatch = true;
        if (filter) {
          switch (filterType) {
            case "CSA":
              textMatch = (employee?.csaName ?? "").toLowerCase().includes(term);
              break;

            case "Company":
              textMatch = (employee?.companyName ?? "").toLowerCase().includes(term);
              break;

            case "Employee":
              textMatch = `${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.toLowerCase().includes(term);
              break;

            default:
              textMatch = true;
          }
        }

        // BLACKLIST FILTER
        let companyMatch = true;
        if (selectedCompanyIdentifiers.length > 0) {
          const companyName = (employee?.companyName ?? "").toLowerCase();
          companyMatch = !selectedCompanyNames.some((name) => companyName.includes(name));
        }

        const eVerifyMatch = !pendingEVerify || (r.hasCompletedI9 && !r.employee?.eVerify);
        const paychexMatch = !pendingPaychex || !r.paychexWorkerId;

        return textMatch && companyMatch && eVerifyMatch && paychexMatch;
      });
    },
    [filter, filterType, selectedCompanyIdentifiers, pendingEVerify, pendingPaychex],
  );

  const filteredRequestsAwaitingAdmin = useMemo(() => {
    return filterRequests(requestsAwaitingAdmin);
  }, [requestsAwaitingAdmin, filterRequests]);

  const filteredRequestsAwaitingEmployee = useMemo(() => {
    return filterRequests(requestsAwaitingEmployee);
  }, [requestsAwaitingEmployee, filterRequests]);

  ///////////////////
  /// API REQUESTS
  ///////////////////

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setIsLoading(true);
    const response = await getDashboardOnboarding();
    if (response.status == 200) {
      setRequestsAwaitingAdmin(response.requestsAwaitingAdmin);
      setRequestsAwaitingEmployee(response.requestsAwaitingEmployee);
      setCompanies(response.companies);
    }

    setIsLoading(false);
  }

  async function handleDownloadDocumentRequest(documentRequest, setIsDownloading) {
    setIsDownloading(documentRequest.uid);
    const response = await downloadDocumentRequestOnboarding(documentRequest.uid);
    if (response.status == 200) {
      const merger = new PDFMerger();

      const sortedDocumentTypes = ["offerLetter", "i9", "w4", "dl", "ssn", "healthDental", "vision", ...Object.keys(taxDocumentDictionary), ...Object.keys(DirectDepositDocuments), "f8850"].filter(
        (d) => Object.keys(response.documents).includes(d),
      );

      for (let i = 0; i < sortedDocumentTypes.length; i++) {
        const document = response.documents[sortedDocumentTypes[i]];
        if (document?.base64) {
          if (!document?.base64.split(",", 1)[0].includes("pdf")) {
            const pdf = (
              <Document>
                <Page size="LETTER" style={{ position: "relative", padding: 50 }}>
                  <Text
                    style={{
                      fontSize: 15,
                      textAlign: "center",
                      marginBottom: 12,
                    }}
                  >
                    {sortedDocumentTypes[i] === "dl" ? "Driver's License" : sortedDocumentTypes[i] === "ssn" ? "Social Security Card" : ""}
                  </Text>
                  <Image style={{ objectFit: "contain", maxHeight: 600 }} src={document.base64} />
                </Page>
              </Document>
            );
            const base64 = await reactPdfToBase64(pdf);
            const fetchResponse = await fetch(base64);
            const blob = await fetchResponse.blob();
            await merger.add(blob);
          } else {
            const fetchResponse = await fetch(document.base64);
            const blob = await fetchResponse.blob();
            await merger.add(blob);
          }
        }
      }

      const mergedPdf = await merger.saveAsBlob();

      const elem = window.document.createElement("a");
      elem.target = "_blank";
      elem.href = window.URL.createObjectURL(mergedPdf);
      elem.download = `${documentRequest.employee?.firstName ?? ""} ${documentRequest.employee?.lastName ?? ""} Document Request ${moment(documentRequest.dateCreated).format("MMM D, YYYY")}`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    }
    setIsDownloading(undefined);
  }

  async function handleDismissRequest(documentRequest, setIsDismissing, setShowConfirmation) {
    setIsDismissing(true);
    const response = await dismissDocumentRequestOnboarding(documentRequest.uid);
    if (response.status == 200) {
      handleRemoveRequest(documentRequest.uid);
      setShowConfirmation(false);
    }
    setIsDismissing(false);
  }

  /////////////////////
  /// HANDLERS
  /////////////////////

  function handleRemoveRequest(documentRequestIdentifier) {
    setRequestsAwaitingAdmin(requestsAwaitingAdmin.filter((d) => d.uid != documentRequestIdentifier));
    setRequestsAwaitingEmployee(requestsAwaitingEmployee.filter((d) => d.uid != documentRequestIdentifier));
  }

  function handleRemoveDocumentRequestDocument(documentRequestIdentifier, documentType) {
    const targetArray = requestsAwaitingAdmin.find((r) => r.uid === documentRequestIdentifier) ? requestsAwaitingAdmin : requestsAwaitingEmployee;

    const newArray = Array.from(targetArray);
    const request = newArray.find((r) => r.uid === documentRequestIdentifier);

    if (request) {
      delete request.documents[documentType];
    }

    if (targetArray === requestsAwaitingAdmin) {
      setRequestsAwaitingAdmin(newArray);
      setSelectedDocumentRequest(request);
    } else {
      setRequestsAwaitingEmployee(newArray);
      setSelectedDocumentRequest(request);
    }
  }

  function handleSetDocumentRequestDocumentIncomplete(documentRequestIdentifier, documentType) {
    const targetArray = requestsAwaitingAdmin.find((r) => r.uid === documentRequestIdentifier) ? requestsAwaitingAdmin : requestsAwaitingEmployee;

    const newArray = Array.from(targetArray);
    const request = newArray.find((r) => r.uid === documentRequestIdentifier);

    const document = request?.documents[documentType];
    if (document) {
      document.employeeCompleted = null;
      if ("employerCompleted" in document) {
        document.employerCompleted = null;
      }
    }

    if (targetArray === requestsAwaitingAdmin) {
      setRequestsAwaitingAdmin(newArray);
      setSelectedDocumentRequest(request);
    } else {
      setRequestsAwaitingEmployee(newArray);
      setSelectedDocumentRequest(request);
    }
  }

  function handleShowModal(documentRequest) {
    setSelectedDocumentRequest(documentRequest);
    setShowDetailsModal(true);
  }

  function toggleCompany(uid) {
    const nextValue = selectedCompanyIdentifiers.includes(uid) ? selectedCompanyIdentifiers.filter((id) => id !== uid) : [...selectedCompanyIdentifiers, uid];
    setSelectedCompanyIdentifiers(nextValue);
  }

  const adminRows = filteredRequestsAwaitingAdmin.map((documentRequest) => {
    return (
      <DocumentRequestElement
        key={documentRequest.uid}
        documentRequest={documentRequest}
        handleShowModal={handleShowModal}
        handleDownloadDocumentRequest={handleDownloadDocumentRequest}
        handleDismissRequest={handleDismissRequest}
        showStatus
        paychexSubmission
        onEVerifySuccess={loadData}
      />
    );
  });

  const employeeRows = filteredRequestsAwaitingEmployee.map((documentRequest) => {
    return (
      <DocumentRequestElement
        key={documentRequest.uid}
        documentRequest={documentRequest}
        handleShowModal={handleShowModal}
        handleDownloadDocumentRequest={handleDownloadDocumentRequest}
        handleDismissRequest={handleDismissRequest}
        onEVerifySuccess={loadData}
      />
    );
  });

  const filterTypeDropdownItems = ["Company", "CSA", "Employee"].map((type) => {
    return (
      <Dropdown.Item
        key={type}
        onClick={() => {
          setFilterType(type);
        }}
        active={filterType == type}
      >
        {type}
      </Dropdown.Item>
    );
  });

  const visibleCount = companies.length - selectedCompanyIdentifiers.length;

  return (
    <LoadingWrapper isLoading={isLoading}>
      <InputGroup style={{ marginBottom: 12 }}>
        <InputGroup.Text>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </InputGroup.Text>
        <Form.Control
          placeholder={`Filter by ${filterType}`}
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        />
        <Dropdown>
          <Dropdown.Toggle>{filterType}</Dropdown.Toggle>
          <Dropdown.Menu>{filterTypeDropdownItems}</Dropdown.Menu>
        </Dropdown>
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary">{companies.length === 0 ? "Select Companies" : `${visibleCount}/${companies.length} Companies`}</Dropdown.Toggle>

          <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
            {[...companies]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((company) => {
                const isSelected = !selectedCompanyIdentifiers.includes(company.uid);

                return (
                  <Dropdown.Item
                    style={{ cursor: "pointer" }}
                    key={company.uid}
                    active={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCompany(company.uid);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{company.name}</span>
                    </div>
                  </Dropdown.Item>
                );
              })}
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown>
          <Dropdown.Toggle variant={pendingEVerify || pendingPaychex ? "primary" : "outline-secondary"}>
            <FontAwesomeIcon icon={faFilter} className="me-1" />
            Status Filters
            {(pendingEVerify || pendingPaychex) && (
              <span className="badge bg-light text-dark ms-2">
                {[pendingEVerify, pendingPaychex].filter(Boolean).length}
              </span>
            )}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item
              active={pendingEVerify}
              onClick={(e) => { e.stopPropagation(); setPendingEVerify((v) => !v); }}
            >
              <FontAwesomeIcon icon={faShield} className="me-2" />
              Pending E-Verify
            </Dropdown.Item>
            <Dropdown.Item
              active={pendingPaychex}
              onClick={(e) => { e.stopPropagation(); setPendingPaychex((v) => !v); }}
            >
              <FontAwesomeIcon icon={faBriefcase} className="me-2" />
              Pending Paychex
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </InputGroup>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <QuickTable
          title="Document Requests Awaiting Admin"
          headers={["Company", "CSA", "State", "Employee", "Date Created", "Documents Requested", "Paychex Worker", "Status", ""]}
          rows={adminRows}
          widths={[null, null, null, null, 1, null, 1, 1, 1]}
          noWrap
          size="sm"
        />
        <QuickTable
          title="Document Requests Awaiting Employee"
          headers={["Company", "CSA", "State", "Employee", "Date Created", "Documents Requested", "Paychex Worker", ""]}
          rows={employeeRows}
          widths={[null, null, null, null, 1, null, 1, 1]}
          noWrap
          size="sm"
        />
      </div>
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="xl">
        <DocumentRequestDetailsOnboarding
          documentRequest={selectedDocumentRequest}
          handleRemoveDocumentRequestDocument={handleRemoveDocumentRequestDocument}
          handleSetDocumentRequestDocumentIncomplete={handleSetDocumentRequestDocumentIncomplete}
        />
      </Modal>
    </LoadingWrapper>
  );
}
